import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, Trash2, Zap } from 'lucide-react';
import * as api from '../services/api';

function EPG() {
  const [sources, setSources] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    priority: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sourcesRes, coverageRes] = await Promise.all([
        api.getEpgSources(),
        api.getEpgCoverage()
      ]);
      setSources(sourcesRes.data);
      setCoverage(coverageRes.data);
    } catch (error) {
      console.error('Failed to load EPG data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createEpgSource(formData);
      setShowModal(false);
      setFormData({ name: '', url: '', priority: 0 });
      loadData();
    } catch (error) {
      alert('Failed to create EPG source: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleFetch = async (id) => {
    try {
      await api.fetchEpgSource(id);
      alert('EPG fetch started! This may take a few minutes.');
      setTimeout(loadData, 2000);
    } catch (error) {
      alert('Fetch failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this EPG source?')) return;
    try {
      await api.deleteEpgSource(id);
      loadData();
    } catch (error) {
      alert('Delete failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAutoMatch = async () => {
    if (!window.confirm('This will automatically match channels to EPG data. Continue?')) return;
    try {
      await api.autoMatchEpg();
      alert('Auto-matching started! This may take a few minutes.');
      setTimeout(loadData, 3000);
    } catch (error) {
      alert('Auto-match failed: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return <div className="loading">Loading EPG data...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>EPG Management</h2>
        <p>Manage Electronic Program Guide sources and coverage</p>
      </div>

      {coverage && (
        <div className="stats-grid" style={{ marginBottom: '30px' }}>
          <div className="stat-card">
            <h3>Overall Coverage</h3>
            <div className="value">{coverage.overall.coverage_percent}%</div>
            <div className="subtext">
              {coverage.overall.mapped} of {coverage.overall.total} channels
            </div>
          </div>

          <div className="stat-card">
            <h3>Mapped Channels</h3>
            <div className="value">{coverage.overall.mapped}</div>
            <div className="subtext">With EPG data</div>
          </div>

          <div className="stat-card">
            <h3>Unmapped Channels</h3>
            <div className="value">{coverage.overall.unmapped}</div>
            <div className="subtext">Missing EPG data</div>
          </div>

          <div className="stat-card">
            <h3>EPG Sources</h3>
            <div className="value">{sources.length}</div>
            <div className="subtext">Active sources</div>
          </div>
        </div>
      )}

      <div className="table-container" style={{ marginBottom: '20px' }}>
        <div className="table-header">
          <h3>EPG Sources</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-success" onClick={handleAutoMatch}>
              <Zap size={16} />
              Auto-Match Channels
            </button>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <Plus size={16} />
              Add EPG Source
            </button>
          </div>
        </div>

        {sources.length === 0 ? (
          <div className="empty-state">
            <h3>No EPG sources yet</h3>
            <p>Add an EPG source to provide program guide data</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Priority</th>
                <th>Channels</th>
                <th>Last Fetched</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((source) => (
                <tr key={source.id}>
                  <td><strong>{source.name}</strong></td>
                  <td>{source.priority}</td>
                  <td>{source.channel_count || 0}</td>
                  <td>{source.last_fetched ? new Date(source.last_fetched).toLocaleString() : 'Never'}</td>
                  <td>
                    <span className={`badge badge-${source.enabled ? 'success' : 'danger'}`}>
                      {source.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-success"
                      style={{ marginRight: '8px', padding: '6px 12px' }}
                      onClick={() => handleFetch(source.id)}
                    >
                      <RefreshCw size={14} />
                    </button>
                    <button
                      className="btn btn-danger"
                      style={{ padding: '6px 12px' }}
                      onClick={() => handleDelete(source.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {coverage?.by_provider && coverage.by_provider.length > 0 && (
        <div className="table-container">
          <div className="table-header">
            <h3>Coverage by Provider</h3>
          </div>
          <table>
            <thead>
              <tr>
                <th>Provider</th>
                <th>Total Channels</th>
                <th>Mapped</th>
                <th>Coverage</th>
              </tr>
            </thead>
            <tbody>
              {coverage.by_provider.map((provider) => (
                <tr key={provider.id}>
                  <td><strong>{provider.name}</strong></td>
                  <td>{provider.total_channels}</td>
                  <td>{provider.mapped_channels}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        flex: 1,
                        height: '8px',
                        background: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${provider.coverage_percent}%`,
                          height: '100%',
                          background: provider.coverage_percent > 80 ? '#10b981' : provider.coverage_percent > 50 ? '#f59e0b' : '#ef4444',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: '600', minWidth: '40px' }}>
                        {provider.coverage_percent}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '500px'
          }}>
            <h3 style={{ marginBottom: '20px' }}>Add EPG Source</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. IPTV-EPG"
                  required
                />
              </div>

              <div className="form-group">
                <label>EPG URL</label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="http://example.com/epg.xml"
                  required
                />
              </div>

              <div className="form-group">
                <label>Priority (higher = checked first)</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                  min="0"
                  max="100"
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Add Source
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default EPG;
